import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  X,
  Check,
  AlertTriangle,
  Clock,
  ChevronDown,
  ChevronRight,
  Search,
  Download,
  Bot,
  User,
  Zap,
  Wrench,
  Loader2,
  XCircle,
  GitPullRequest,
} from 'lucide-react';
import { format } from 'date-fns';
import { useThresholdDetailsPanel } from '../../contexts/ThresholdDetailsPanelContext';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { useOrganization } from '../../contexts/OrganizationContext';
import { apiEndpoints } from '../../utils/api';
import {
  ThresholdDetailsResponse,
  FindingWithTimeline,
  TimelineEvent,
  SlaStatus,
  ThresholdBreakdown,
  ThresholdSummary,
} from '../../types/compliance';
import { Spinner } from '../../design-system';

// ============================================================================
// Status Badge Components
// ============================================================================

interface SlaStatusBadgeProps {
  status: SlaStatus;
  size?: 'sm' | 'md';
}

const SlaStatusBadge: React.FC<SlaStatusBadgeProps> = ({ status, size = 'md' }) => {
  // For audit: only 3 statuses matter
  // - resolved_within_sla: Resolved before deadline (good)
  // - breached_open: Open past deadline (bad)
  // - breached_resolved: Resolved past deadline (bad)
  const getConfig = (): { label: string; className: string } => {
    if (status === 'resolved_within_sla') {
      return { label: 'Resolved within SLA', className: 'bg-brand-primary/10 text-brand-primary' };
    }
    if (status === 'breached_open') {
      return { label: 'Breached (Open)', className: 'bg-red-500/10 text-red-500' };
    }
    if (status === 'breached_resolved') {
      return { label: 'Breached (Resolved)', className: 'bg-orange-500/10 text-orange-500' };
    }
    return { label: status, className: 'bg-gray-500/10 text-gray-500' };
  };

  const config = getConfig();
  const sizeClass = size === 'sm' ? 'text-xs px-1.5 py-0.5' : 'text-xs px-2 py-1';

  return (
    <span className={`${sizeClass} rounded-full whitespace-nowrap ${config.className}`}>
      {config.label}
    </span>
  );
};

// ============================================================================
// Breakdown Pills Component
// ============================================================================

interface BreakdownPillsProps {
  breakdown: ThresholdBreakdown;
  selectedStatus: SlaStatus | null;
  onStatusClick: (status: SlaStatus | null) => void;
}

const BreakdownPills: React.FC<BreakdownPillsProps> = ({
  breakdown,
  selectedStatus,
  onStatusClick,
}) => {
  const pills: { status: SlaStatus | null; count: number; label: string }[] = [
    { status: null, count: breakdown.total, label: 'All' },
    { status: 'resolved_within_sla', count: breakdown.withinSla, label: 'Resolved within SLA' },
    { status: 'breached_open', count: breakdown.breachedOpen, label: 'Breached (Open)' },
    { status: 'breached_resolved', count: breakdown.breachedResolved, label: 'Breached (Resolved)' },
  ];

  return (
    <div className="flex flex-wrap gap-2">
      {pills.map(pill => (
        <button
          key={pill.label}
          onClick={() => onStatusClick(pill.status)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            selectedStatus === pill.status
              ? 'bg-brand-primary text-white'
              : 'bg-ui-background-tertiary text-ui-text-secondary hover:bg-ui-background-secondary'
          }`}
        >
          {pill.label} ({pill.count})
        </button>
      ))}
    </div>
  );
};

// ============================================================================
// Timeline Event Component
// ============================================================================

interface TimelineEventRowProps {
  event: TimelineEvent;
  isLast: boolean;
}

const TimelineEventRow: React.FC<TimelineEventRowProps> = ({ event, isLast }) => {
  const getEventIcon = (eventType: string) => {
    if (eventType === 'detected' || eventType === 'reopened') {
      return <AlertTriangle className="w-3 h-3 text-amber-500" />;
    }
    if (eventType.startsWith('resolved')) {
      return <Check className="w-3 h-3 text-brand-primary" />;
    }
    if (eventType === 'assessed') {
      return <Bot className="w-3 h-3 text-purple-500" />;
    }
    if (eventType === 'sla_approaching') {
      return <Clock className="w-3 h-3 text-amber-500" />;
    }
    if (eventType === 'sla_breached') {
      return <AlertTriangle className="w-3 h-3 text-red-500" />;
    }
    // Fix events
    if (eventType === 'fix_created') {
      return <Wrench className="w-3 h-3 text-blue-500" />;
    }
    if (eventType === 'fix_started') {
      return <Loader2 className="w-3 h-3 text-blue-500" />;
    }
    if (eventType === 'fix_completed') {
      return <Check className="w-3 h-3 text-green-500" />;
    }
    if (eventType === 'fix_failed') {
      return <XCircle className="w-3 h-3 text-red-500" />;
    }
    if (eventType === 'fix_pr_created') {
      return <GitPullRequest className="w-3 h-3 text-purple-500" />;
    }
    return <Zap className="w-3 h-3 text-ui-text-tertiary" />;
  };

  const getActorIcon = (actorType: string) => {
    if (actorType === 'ai') {
      return <Bot className="w-3 h-3" />;
    }
    if (actorType === 'user') {
      return <User className="w-3 h-3" />;
    }
    return <Zap className="w-3 h-3" />;
  };

  const formatEventType = (eventType: string): string => {
    return eventType
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div className="flex gap-3">
      {/* Timeline connector */}
      <div className="flex flex-col items-center">
        <div className="w-6 h-6 rounded-full bg-ui-background-tertiary flex items-center justify-center">
          {getEventIcon(event.eventType)}
        </div>
        {!isLast && <div className="w-0.5 flex-1 bg-ui-border-primary mt-1" />}
      </div>

      {/* Event content */}
      <div className="flex-1 pb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ui-text-primary">
            {formatEventType(event.eventType)}
          </span>
          <span className="flex items-center gap-1 text-xs text-ui-text-tertiary">
            {getActorIcon(event.actorType)}
            {event.actorDisplayName}
          </span>
        </div>
        <p className="text-xs text-ui-text-tertiary mt-0.5">
          {format(new Date(event.eventTimestamp), 'MMM d, yyyy h:mm a')}
        </p>
        {Object.keys(event.details).length > 0 && (() => {
          const scanType = event.details.scan_type;
          const severity = event.details.severity;
          const prUrl = event.details.pr_url as string | undefined;
          const prNumber = event.details.pr_number as number | undefined;
          const fixTitle = event.details.fix_title as string | undefined;
          const repoFullName = event.details.repo_full_name as string | undefined;
          return (
            <div className="mt-1 text-xs text-ui-text-secondary">
              {scanType != null && (
                <span className="mr-2">Scan: {String(scanType)}</span>
              )}
              {severity != null && (
                <span className="mr-2">Severity: {String(severity)}</span>
              )}
              {fixTitle != null && (
                <span className="mr-2">Fix: {fixTitle}</span>
              )}
              {prUrl && prNumber && (
                <a
                  href={prUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-primary hover:underline"
                >
                  PR #{prNumber}{repoFullName ? ` in ${repoFullName}` : ''}
                </a>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

// ============================================================================
// Finding Row Component
// ============================================================================

// Debug flag - reads from REACT_APP_DEBUG_COMPLIANCE env var
const DEBUG_COMPLIANCE = process.env.REACT_APP_DEBUG_COMPLIANCE === 'true';

interface FindingRowProps {
  finding: FindingWithTimeline;
  isExpanded: boolean;
  onToggle: () => void;
}

const FindingRow: React.FC<FindingRowProps> = ({ finding, isExpanded, onToggle }) => {
  return (
    <div className="border border-ui-border-primary rounded-lg overflow-hidden">
      {/* Debug: Show finding ID */}
      {DEBUG_COMPLIANCE && (
        <div className="bg-yellow-500/10 px-3 py-1 text-xs font-mono text-yellow-600 border-b border-yellow-500/20">
          finding_id: {finding.findingId} | cycle_id: {finding.cycleId}
        </div>
      )}
      {/* Finding header */}
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-3 p-3 hover:bg-ui-background-tertiary transition-colors text-left"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-ui-text-tertiary mt-1 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-ui-text-tertiary mt-1 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <SlaStatusBadge status={finding.slaStatus} size="sm" />
            <span className="text-xs text-ui-text-tertiary font-mono truncate">
              {finding.checkId}
            </span>
          </div>
          <p className="text-sm text-ui-text-primary mt-1 line-clamp-2">
            {finding.message}
          </p>
          <div className="flex items-center gap-3 mt-2 text-xs text-ui-text-tertiary">
            <span className="truncate max-w-[200px]" title={finding.filePath}>
              {finding.filePath}
              {finding.lineNumber && `:${finding.lineNumber}`}
            </span>
            <span className="text-ui-text-secondary">
              {finding.applicationName}
            </span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          {finding.daysOverdue !== null && finding.daysOverdue > 0 ? (
            <span className="text-xs text-red-500 font-medium">
              {finding.daysOverdue}d overdue
            </span>
          ) : finding.daysRemaining !== null ? (
            <span className="text-xs text-ui-text-secondary">
              {finding.daysRemaining}d remaining
            </span>
          ) : null}
          <p className="text-xs text-ui-text-tertiary mt-0.5">
            SLA: {finding.slaTargetDays}d
          </p>
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-ui-border-primary p-4 bg-ui-background-secondary/30">
          {/* Details grid */}
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div>
              <p className="text-xs text-ui-text-tertiary">Detected</p>
              <p className="text-ui-text-primary">
                {format(new Date(finding.detectedAt), 'MMM d, yyyy h:mm a')}
              </p>
            </div>
            {finding.resolvedAt && (
              <div>
                <p className="text-xs text-ui-text-tertiary">Resolved</p>
                <p className="text-ui-text-primary">
                  {format(new Date(finding.resolvedAt), 'MMM d, yyyy h:mm a')}
                </p>
              </div>
            )}
            <div>
              <p className="text-xs text-ui-text-tertiary">SLA Deadline</p>
              <p className="text-ui-text-primary">{finding.slaDeadline}</p>
            </div>
            {finding.actualBusinessDays !== null && (
              <div>
                <p className="text-xs text-ui-text-tertiary">Time to Resolve</p>
                <p className="text-ui-text-primary">
                  {finding.actualBusinessDays} business days
                </p>
              </div>
            )}
          </div>

          {/* Assessment info */}
          {finding.assessment && (
            <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <p className="text-xs font-medium text-purple-400 mb-2">AI Assessment</p>
              <div className="flex flex-wrap gap-2 text-xs">
                {finding.assessment.isRealConcern && (
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded border border-purple-500/30">
                    Real Concern: <span className="font-medium text-purple-200">{finding.assessment.isRealConcern}</span>
                  </span>
                )}
                {finding.assessment.assessedPriority && (
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded border border-purple-500/30">
                    Priority: <span className="font-medium text-purple-200">{finding.assessment.assessedPriority}</span>
                  </span>
                )}
                {finding.assessment.shouldFix && (
                  <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded border border-purple-500/30">
                    Should Fix: <span className="font-medium text-purple-200">{finding.assessment.shouldFix}</span>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div>
            <p className="text-xs font-medium text-ui-text-secondary mb-3">Event Timeline</p>
            <div className="pl-2">
              {finding.timeline.map((event, idx) => (
                <TimelineEventRow
                  key={event.id}
                  event={event}
                  isLast={idx === finding.timeline.length - 1}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Panel Component
// ============================================================================

export const ThresholdDetailsPanel: React.FC = () => {
  const { isOpen, params, closePanel } = useThresholdDetailsPanel();
  const api = useAuthenticatedApi();
  const { currentOrganization } = useOrganization();

  // State
  const [data, setData] = useState<ThresholdDetailsResponse | null>(null);
  const [allFindings, setAllFindings] = useState<FindingWithTimeline[]>([]); // All loaded findings for client-side filtering
  const [originalSummary, setOriginalSummary] = useState<ThresholdBreakdown | null>(null); // Unfiltered counts for pills
  const [originalThreshold, setOriginalThreshold] = useState<ThresholdSummary | null>(null); // Unfiltered threshold for header
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedFindings, setExpandedFindings] = useState<Set<string>>(new Set());
  const [selectedStatus, setSelectedStatus] = useState<SlaStatus | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [committedSearch, setCommittedSearch] = useState(''); // Search value to use for API calls
  const [offset, setOffset] = useState(0);
  const limit = 100; // Fetch more initially for better client-side filtering

  // Ref to track if initial data has been loaded (avoids stale closure issues)
  const hasInitialDataRef = React.useRef(false);

  // Fetch data from API (only for initial load, search, and load more)
  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!isOpen || !params || !currentOrganization) return;

    // Skip API call if we have data and just changing status filter (use client-side filtering)
    // Using ref instead of allFindings.length to avoid stale closure and infinite loop issues
    const isStatusFilterOnly = !forceRefresh && hasInitialDataRef.current && !committedSearch && offset === 0;
    if (isStatusFilterOnly) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get<ThresholdDetailsResponse>(
        apiEndpoints.compliance.thresholdDetails(
          currentOrganization.id,
          params.scanType,
          params.severity,
          {
            days: 30,
            // Don't filter by status on server - we do client-side filtering
            search: committedSearch || undefined,
            limit,
            skip: offset,
            applicationIds: params.applicationIds,
          }
        )
      );

      // Store original data for header and pill counts (only on initial unfiltered load)
      if (!committedSearch && offset === 0) {
        setOriginalSummary(response.summary);
        setOriginalThreshold(response.threshold);
      }

      // Append findings for "load more", otherwise replace
      if (offset > 0) {
        setAllFindings(prev => [...prev, ...response.findings]);
        setData(prevData => prevData ? {
          ...response,
          findings: [...prevData.findings, ...response.findings],
        } : response);
      } else {
        // New query (initial or search) - replace all findings
        setAllFindings(response.findings);
        setData(response);
        // Mark that we have initial data (for status filter optimization)
        hasInitialDataRef.current = true;
      }
    } catch (err: unknown) {
      console.error('Failed to fetch threshold details:', err);
      setError('Failed to load threshold details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [api, currentOrganization, isOpen, params, offset, committedSearch]);

  // Initial fetch when panel opens or search changes
  useEffect(() => {
    if (isOpen && params && currentOrganization) {
      fetchData(true);
    }
  }, [isOpen, params, currentOrganization, committedSearch, fetchData]);

  // Load more
  useEffect(() => {
    if (offset > 0) {
      fetchData();
    }
  }, [offset, fetchData]);

  // Client-side filtering for status pills (instant)
  const filteredFindings = useMemo(() => {
    if (!selectedStatus) return allFindings;
    return allFindings.filter(f => f.slaStatus === selectedStatus);
  }, [allFindings, selectedStatus]);

  // Reset state when panel closes
  useEffect(() => {
    if (!isOpen) {
      setExpandedFindings(new Set());
      setSelectedStatus(null);
      setSearchQuery('');
      setCommittedSearch('');
      setOffset(0);
      setData(null);
      setAllFindings([]);
      setOriginalSummary(null);
      hasInitialDataRef.current = false;
      setOriginalThreshold(null);
      setError(null);
    }
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closePanel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closePanel]);

  const toggleFinding = (findingId: string) => {
    setExpandedFindings(prev => {
      const next = new Set(prev);
      if (next.has(findingId)) {
        next.delete(findingId);
      } else {
        next.add(findingId);
      }
      return next;
    });
  };

  const handleStatusFilter = (status: SlaStatus | null) => {
    setSelectedStatus(status);
    setOffset(0);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setOffset(0);
    setCommittedSearch(searchQuery);
  };

  const handleLoadMore = () => {
    if (data?.pagination.hasMore) {
      setOffset(prev => prev + limit);
    }
  };

  const handleExportCSV = () => {
    if (!data) return;

    // Escape CSV field: wrap in quotes and escape internal quotes by doubling them
    const escapeCSVField = (field: string | null | undefined): string => {
      const str = field ?? '';
      // Replace any double quotes with two double quotes, then wrap in quotes
      return `"${str.replace(/"/g, '""')}"`;
    };

    const headers = ['Finding ID', 'Check ID', 'File Path', 'SLA Status', 'Days Overdue', 'Detected At', 'Resolved At', 'Repository'];
    const rows = data.findings.map(f => [
      f.findingId,
      f.checkId,
      f.filePath,
      f.slaStatus,
      f.daysOverdue?.toString() || '',
      f.detectedAt,
      f.resolvedAt || '',
      f.applicationName,
    ]);

    const csv = [
      headers.map(escapeCSVField).join(','),
      ...rows.map(r => r.map(escapeCSVField).join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `threshold-details-${params?.scanType}-${params?.severity}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={closePanel}
      />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 w-full max-w-2xl bg-ui-background-primary shadow-xl z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-ui-border-primary">
          <div>
            <h2 className="text-lg font-semibold text-ui-text-primary">
              {params?.label || 'Threshold Details'}
            </h2>
            {data && (() => {
              // Use original threshold to prevent values changing when filtering
              const threshold = originalThreshold || data.threshold;
              return (
                <div className="flex items-center gap-3 mt-1">
                  <span className={`text-2xl font-bold ${
                    threshold.status === 'passing' ? 'text-brand-primary' : 'text-red-500'
                  }`}>
                    {threshold.actual}%
                  </span>
                  <span className="text-sm text-ui-text-tertiary">
                    / {threshold.target}% target
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    threshold.status === 'passing'
                      ? 'bg-brand-primary/10 text-brand-primary'
                      : 'bg-red-500/10 text-red-500'
                  }`}>
                    {threshold.status === 'passing' ? 'Passing' : 'Failing'}
                  </span>
                </div>
              );
            })()}
          </div>
          <button
            onClick={closePanel}
            className="p-2 hover:bg-ui-background-tertiary rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-ui-text-tertiary" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && !data ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size={32} />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500 mb-4">{error}</p>
              <button
                onClick={() => fetchData(true)}
                className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90"
              >
                Retry
              </button>
            </div>
          ) : data ? (
            <div className="space-y-4">
              {/* Breakdown pills - use originalSummary for counts to keep them consistent when filtering */}
              <BreakdownPills
                breakdown={originalSummary || data.summary}
                selectedStatus={selectedStatus}
                onStatusClick={handleStatusFilter}
              />

              {/* Search */}
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-text-tertiary" />
                <input
                  type="text"
                  placeholder="Search by file path or check ID..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-ui-background-secondary border border-ui-border-primary rounded-lg text-sm text-ui-text-primary placeholder:text-ui-text-tertiary focus:outline-none focus:border-brand-primary"
                />
              </form>

              {/* Findings list */}
              <div className="space-y-3">
                {filteredFindings.length === 0 ? (
                  <div className="text-center py-8 text-ui-text-tertiary">
                    No findings match the current filters.
                  </div>
                ) : (
                  <>
                    {filteredFindings.map(finding => (
                      <FindingRow
                        key={finding.cycleId}
                        finding={finding}
                        isExpanded={expandedFindings.has(finding.cycleId)}
                        onToggle={() => toggleFinding(finding.cycleId)}
                      />
                    ))}

                    {/* Load more - only show when viewing all (no status filter) */}
                    {!selectedStatus && data.pagination.hasMore && (
                      <button
                        onClick={handleLoadMore}
                        disabled={isLoading}
                        className="w-full py-3 text-sm text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {isLoading ? 'Loading...' : 'Load More'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {data && (
          <div className="flex items-center justify-between p-4 border-t border-ui-border-primary">
            <span className="text-sm text-ui-text-tertiary">
              Showing {filteredFindings.length} of {selectedStatus ? filteredFindings.length : data.pagination.total} findings
            </span>
            <div className="flex gap-2">
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-ui-text-secondary hover:bg-ui-background-tertiary rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
